import React, { useEffect, useState } from 'react';
import {
  Card,
  Icon,
  Loader,
  Dimmer,
  Image,
  Segment,
  Modal,
  Form,
  Message,
  Button,
} from 'semantic-ui-react';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import NestedAccordionToggle from 'components/common/AccordionToggle/NestedAccordionToggle';
import getUserRolesAction from 'redux/actions/user/getUserRoles';
import getRoleType from 'helpers/getRoleType';
import toggleUserRoles from 'redux/actions/user/toggleUserRoles';
import dimmer from 'assets/images/short-paragraph.png';
import toggleRolesInBulk from 'redux/actions/user/toggleRolesInBulk';
import PINInput from 'components/common/PINInput';
import userRolesFormatter from 'helpers/shapeFormatter/userRoles';
import { canPerformAction } from 'helpers/permissions';
import permissions from 'constants/permissions';
import getUserRoles from 'helpers/getUserRoles';

const UserRoles = () => {
  const [actionForm, setActionForm] = useState({});
  const [pinModal, setPinModal] = useState(false);
  const params = useParams();
  const dispatch = useDispatch();

  const { data, loading } = useSelector(
    ({ user: { singleUserRoles } }) => singleUserRoles,
  );

  const { loading: loadingBulk, error: errorBulk } = useSelector(
    ({ user: { toggleRolesInBulk } }) => toggleRolesInBulk,
  );

  const { loading: loadingToggle, error: errorToggle } = useSelector(
    ({ user: { toggleUserRoles } }) => toggleUserRoles,
  );

  const { currentOrgId, roles, orgUsers } = getUserRoles();

  useEffect(() => {
    getUserRolesAction(params?.userId)(dispatch);
  }, [params?.userId]);

  const formattedData = data
    ? userRolesFormatter({ data, params })
    : [];

  const handleChange = (status, role, items, context, title) => {
    setPinModal(true);
    const roleType = getRoleType(role);
    const categoryData = formattedData.find(cat =>
      cat.subItems.some(item => item.context === role),
    );
    setActionForm({
      status,
      items,
      context,
      roleType,
      title,
      category: categoryData?.category,
    });
  };

  const handlePassword = (e, { name, value }) => {
    setActionForm({
      ...actionForm,
      [name]: value,
    });
  };

  const toggleRoles = () => {
    const action = actionForm?.status ? 'activate' : 'deactivate';
    const roleType =
      actionForm?.category === 'group_member'
        ? 'group_member'
        : actionForm?.roleType?.type;

    switch (actionForm.context) {
      case 'toggleBulk':
        return toggleRolesInBulk(params?.userId, action, {
          type: roleType,
          password: actionForm?.password,
        })(dispatch, setPinModal);

      case 'toggleRole': {
        const payload = {
          type: roleType,
          password: actionForm?.password,
        };
        if (
          actionForm?.category !== 'save_admin' &&
          actionForm?.roleType?.dependency1 &&
          actionForm?.items?.id
        ) {
          payload[actionForm.roleType.dependency1] =
            actionForm.items.id;
          if (
            actionForm?.roleType?.dependency2 &&
            actionForm?.items?.organization_id
          ) {
            payload[actionForm.roleType.dependency2] =
              actionForm.items.organization_id;
          }
        }
        return toggleUserRoles(
          params?.userId,
          action,
          payload,
        )(dispatch, setPinModal);
      }

      case 'nestedToggle':
        return actionForm?.roles?.map(role =>
          toggleRolesInBulk(params?.userId, action, {
            type: role,
            password: actionForm?.password,
          })(dispatch, setPinModal),
        );

      default:
        return null;
    }
  };

  const toggleBulk = (status, mainItems, subItems, context) => {
    setPinModal(true);
    return mainItems?.map(item =>
      setActionForm({
        status,
        context,
        items: item?.subItems,
        roles: item?.roles,
        title: data?.user?.first_name,
      }),
    );
  };

  const canEditUserRoles = () =>
    canPerformAction(
      permissions.canPerformAction.editUserRoles,
      roles,
      {
        currentOrgId,
        orgUsers,
      },
    );

  return (
    <>
      <Modal
        size="mini"
        open={pinModal}
        onClose={() => setPinModal(false)}
      >
        <Modal.Header>
          {actionForm?.status ? 'Activate' : 'Deactivate'} user role
          <Icon
            name="close"
            className="pointer"
            onClick={() => setPinModal(false)}
          />
        </Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to{' '}
            {actionForm?.status ? 'activate' : 'deactivate'}{' '}
            {data?.user?.first_name} as {actionForm?.title}{' '}
            {actionForm?.items?.length > 0 ? (
              <>
                of{' '}
                {actionForm?.items?.map(item => (
                  <b key={item?.id}>{item?.name}, </b>
                ))}
              </>
            ) : actionForm?.items?.name ? (
              <>
                of <b>{actionForm?.items?.name}</b>
              </>
            ) : null}
          </p>
          <Form
            onSubmit={toggleRoles}
            loading={loadingBulk || loadingToggle}
          >
            <PINInput
              className="input"
              fluid
              size="tiny"
              label="Enter Pin"
              name="password"
              onInputChange={handlePassword}
            />

            {(errorBulk || errorToggle) &&
              (errorBulk || errorToggle) === 'Wrong PIN.' && (
                <p color="red">
                  <Message negative>
                    {errorBulk || errorToggle}
                  </Message>
                </p>
              )}

            <Button
              className="confirm"
              type="submit"
              color="blue"
              fluid
            >
              Confirm
            </Button>
          </Form>
        </Modal.Content>
      </Modal>
      <div className="SingleUser__body__details">
        {loading ? (
          <Card>
            <Segment>
              <Dimmer active inverted>
                <Loader inverted />
              </Dimmer>
              <Image src={dimmer} />
            </Segment>
          </Card>
        ) : (
          <Card>
            <Card.Content>
              {formattedData.map(categoryData => (
                <NestedAccordionToggle
                  key={categoryData.category}
                  data={categoryData}
                  toggleBulk={toggleBulk}
                  handleChange={handleChange}
                  disabled={
                    categoryData.category === 'save_admin'
                      ? !canPerformAction(
                          permissions.canPerformAction
                            .editAdminUserRoles,
                          roles,
                          {
                            currentOrgId,
                            orgUsers,
                          },
                        )
                      : !canEditUserRoles()
                  }
                  linkTo={categoryData.linkTo}
                />
              ))}
            </Card.Content>
          </Card>
        )}
      </div>
    </>
  );
};

export default UserRoles;
